/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  currentDocument: {
    plainText: '',
    timestamp: null,
    wordCount: 0,
    characterCount: 0,
    nodes: [],
    html: '',
  },
  isLoading: false,
  hasUnsavedChanges: false,
  lastSaved: null,
  editorStates: {}, // HashMap to store editor states by ID
  /** Scene ids (Lexical id and/or Mongo _id) for which unmount/tab-hidden persist must not PUT (delete/merge). */
  sceneEditorUnmountPersistSkipIds: {},
};

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    setDocumentData: (state, action) => {
      state.currentDocument = {
        ...action.payload,
        timestamp: action.payload.timestamp || new Date().toISOString(),
      };
      state.hasUnsavedChanges = false;
      state.lastSaved = new Date().toISOString();
    },
    updateDocumentData: (state, action) => {
      state.currentDocument = {
        ...state.currentDocument,
        ...action.payload,
        timestamp: new Date().toISOString(),
      };
      state.hasUnsavedChanges = true;
    },
    clearDocument: (state) => {
      state.currentDocument = {
        plainText: '',
        timestamp: new Date().toISOString(),
        wordCount: 0,
        characterCount: 0,
        nodes: [],
        html: '',
      };
      state.hasUnsavedChanges = false;
      state.lastSaved = new Date().toISOString();
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    markAsSaved: (state) => {
      state.hasUnsavedChanges = false;
      state.lastSaved = new Date().toISOString();
    },
    setUnsavedChanges: (state, action) => {
      state.hasUnsavedChanges = action.payload;
    },
    setEditorState: (state, action) => {
      const { id, ...editorState } = action.payload;
      if (id) {
        // Ensure editorStates exists
        if (!state.editorStates) {
          state.editorStates = {};
        }
        state.editorStates[id] = {
          ...editorState,
          timestamp: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
        };
      }
    },
    removeEditorState: (state, action) => {
      const id = action.payload;
      if (id && state.editorStates) {
        delete state.editorStates[id];
      }
      if (id) {
        if (!state.sceneEditorUnmountPersistSkipIds) {
          state.sceneEditorUnmountPersistSkipIds = {};
        }
        state.sceneEditorUnmountPersistSkipIds[id] = true;
      }
    },
    consumeSceneEditorUnmountPersistSkip: (state, action) => {
      const id = action.payload;
      if (id && state.sceneEditorUnmountPersistSkipIds) {
        delete state.sceneEditorUnmountPersistSkipIds[id];
      }
    },
  },
});

export const {
  setDocumentData,
  updateDocumentData,
  clearDocument,
  setLoading,
  markAsSaved,
  setUnsavedChanges,
  setEditorState,
  removeEditorState,
  consumeSceneEditorUnmountPersistSkip,
} = editorSlice.actions;

// Selectors
export const selectCurrentDocument = (state) => state.editor.currentDocument;
export const selectHasUnsavedChanges = (state) => state.editor.hasUnsavedChanges;
export const selectIsLoading = (state) => state.editor.isLoading;
export const selectLastSaved = (state) => state.editor.lastSaved;
export const selectEditorStates = (state) => state.editor.editorStates;
export const selectEditorStateById = (state, id) => state.editor.editorStates[id];
export const selectEditorStatesByChapterId = (state, chapterId) => {
  const allStates = state.editor.editorStates;
  if (!allStates) return {};
  return Object.entries(allStates).reduce((acc, [id, editorState]) => {
    if (editorState.chapterId === chapterId) {
      acc[id] = editorState;
    }
    return acc;
  }, {});
};

export default editorSlice.reducer;
